import { Box, IconButton, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import { Icon } from "@iconify/react";
import {
  CardOffersSection,
  MyCardsSection,
} from "../../components/pages/cards";
import { useTelegram } from "../../hooks";
import { FeatureGuard } from "../../components/shared";
import { useFeatureFlags } from "../../contexts/FeatureFlagsContext";
import { getMyCards } from "../../services/cards";
import { useTranslation } from "react-i18next";

export function CardsPage() {
  const { tg } = useTelegram();
  const navigate = useNavigate();
  const { isFeatureEnabled, getFeatureMessage } = useFeatureFlags();
  const { t } = useTranslation("cards");
  const isMobile =
    tg.platform !== "tdesktop" &&
    tg.platform !== "weba" &&
    tg.platform !== "macos";

  // Секция «Оформить карту» скрывается, если выключен именно `card_issue`
  // (админ может оставить просмотр карт, но временно закрыть заказы).
  const canIssue = isFeatureEnabled("card_issue");
  const issueMessage = getFeatureMessage("card_issue");

  // Тот же queryKey, что и в `MyCardsSection` — react-query переиспользует
  // кэш и не делает второй запрос. Нужно нам, чтобы понимать: есть ли у
  // юзера уже выпущенные карты (тогда прячем полный блок офферов и
  // показываем «+» в шапке).
  const { data: cards } = useQuery({
    queryFn: getMyCards,
    queryKey: ["my-cards"],
  });
  const hasCards = (cards?.length ?? 0) > 0;

  return (
    <FeatureGuard feature="cards">
      <Box
        sx={{
          display: "grid",
          gap: "24px",
          width: "100%",
          marginTop: isMobile ? "40px" : "20px",
          marginBottom: "120px",
        }}
      >
        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginTop: "6px",
            minHeight: 36,
          }}
        >
          <Typography
            sx={{ fontSize: "24px", fontWeight: 700, textAlign: "center" }}
          >
            {t("title")}
          </Typography>

          {/* «+» появляется только когда у юзера уже есть хотя бы одна карта
             (иначе секция офферов ниже открыта, дублировать нет смысла) и
             админ не выключил выпуск. Позиционирован абсолютно, чтобы не
             сдвигать центрированный заголовок. */}
          {hasCards && canIssue && (
            <IconButton
              onClick={() => navigate("/cards/issue")}
              aria-label={t("issue_new_aria")}
              sx={{
                position: "absolute",
                right: 0,
                width: 36,
                height: 36,
                color: "text.primary",
                bgcolor: "transparent",
                "&:hover": { bgcolor: "transparent" },
              }}
            >
              <Icon icon="solar:add-square-outline" width={26} height={26} />
            </IconButton>
          )}
        </Box>

        <MyCardsSection />

        {/* Если у юзера уже есть карты — блок офферов уходит на /cards/issue
           (по клику на «+»). Показываем его тут только для «пустого» кейса
           или когда выпуск отключён (тогда рисуем плашку-заглушку). */}
        {!hasCards &&
          (canIssue ? (
            <CardOffersSection />
          ) : (
            <IssueDisabledNotice
              message={issueMessage ?? t("issue_disabled_default")}
            />
          ))}
      </Box>
    </FeatureGuard>
  );
}

// Небольшая плашка вместо секции «Оформить карту», когда выпуск отключён.
function IssueDisabledNotice({ message }: { message: string }) {
  const { t } = useTranslation("cards");
  return (
    <Box
      sx={(theme) => ({
        padding: "20px",
        borderRadius: "20px",
        backgroundColor: "secondary.main",
        border:
          theme.palette.mode === "light"
            ? "1px solid rgba(0, 0, 0, 0.06)"
            : "none",
        textAlign: "center",
      })}
    >
      <Typography sx={{ fontWeight: 600, marginBottom: "6px" }}>
        {t("issue_disabled_title")}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {message}
      </Typography>
    </Box>
  );
}
